'use strict';

const libPictProvider = require('pict-provider');

const libProvenance = require('../config-editor/Config-Provenance.js');
const libCatalog = require('../config-editor/Config-Namespace-Catalog.js');
const libDictionary = require('../config-editor/Config-Dictionary.js');

// Scopes this tool authors, general -> specific. Contract/Project are included in the cascade only
// when a context id is supplied (otherwise they don't apply to the viewer's own session).
const WRITE_SCOPES = [ 'Global', 'Customer', 'Project', 'User' ];
const SCOPE_LABELS =
{
	Global: 'Platform Default',
	Customer: 'Customer',
	Contract: 'Contract',
	Project: 'Project',
	User: 'My Settings',
};

/**
 * ConfigViewerProvider — the data layer behind the provenance-aware configuration viewer.
 *
 * Reads the cascading Configuration service (api/config) for a chosen NamespaceKey/NamespaceValue at a
 * chosen context, computes per-leaf PROVENANCE (which scope set each value) by diffing the merge at
 * each successive scope (Config-Provenance.computeProvenanceByDiff — robust, server-scoped; the raw
 * ConfigEntry table has no IDCustomer column so a direct row read can't be customer-scoped reliably),
 * writes edits to a chosen scope (the service diffs vs the parent), and also surfaces a record's
 * CustomProperties JSON blob as a flat (un-cascaded) editable object. Mirrors the permission + REST
 * patterns of MasterList-Registry-Provider.
 */
class ConfigViewerProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;
	}

	// --- lifecycle ---

	onAfterInitialize()
	{
		this._ensureState();
		return super.onAfterInitialize();
	}

	_ensureState()
	{
		if (!this.pict.AppData.ConfigViewer)
		{
			this.pict.AppData.ConfigViewer =
			{
				Context: { IDCustomer: this.idCustomer, IDProject: 0, IDUser: this.idUser },
				WriteScope: this.defaultScope,
				Selected: null,        // { Mode, NamespaceKey, NamespaceValue, Entity, RecordID, Label }
				ValueList: [],         // NamespaceValues for the selected key
				EditingConfig: {},     // bound to the ObjectEditor
				ProvenanceMap: {},     // editorPath -> { Scope, ScopeID }
				Loading: false,
				Message: '',
				// view-derived slots (filled by deriveRenderState)
				RailGroups: [],
				ScopeOptions: [],
				HelpNamespace: '',
				HelpKey: '',
			};
		}
		return this.pict.AppData.ConfigViewer;
	}

	get state()
	{
		return this._ensureState();
	}

	// --- session context + permissions (mirror the server config gate) ---

	get restClient()
	{
		return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
	}

	get currentUser()
	{
		return (this.pict && this.pict.AppData && this.pict.AppData.CurrentUser) || {};
	}

	get idCustomer()
	{
		return this.currentUser.CustomerID;
	}

	get idUser()
	{
		return this.currentUser.UserID;
	}

	get isSuperUser()
	{
		const tmpUser = this.currentUser;
		return (Number(tmpUser.UserRoleIndex) === 5) && (Number(tmpUser.CustomerID) === 1);
	}

	get hasCustomerAdmin()
	{
		const tmpRoles = this.currentUser.UserRoles;
		return Array.isArray(tmpRoles) && tmpRoles.some((pRole) => pRole && (pRole.PermissionLevel === 'CustomerAdmin'));
	}

	/**
	 * Can the current user WRITE config at this scope? UI gate only — the server is authoritative.
	 * @param {String} pLevel
	 * @return {Boolean}
	 */
	canWriteScope(pLevel)
	{
		switch (pLevel)
		{
			case 'Global': return this.isSuperUser;
			case 'Customer':
			case 'Contract':
			case 'Project': return this.isSuperUser || this.hasCustomerAdmin;
			case 'User': return true;
			default: return false;
		}
	}

	get defaultScope()
	{
		if (this.isSuperUser) { return 'Global'; }
		if (this.hasCustomerAdmin) { return 'Customer'; }
		return 'User';
	}

	_scopeLabel(pLevel)
	{
		return SCOPE_LABELS[pLevel] || pLevel;
	}

	/**
	 * The LevelContextID for a scope, from the active context.
	 * @param {String} pLevel
	 * @return {Number}
	 */
	_levelIdFor(pLevel)
	{
		const tmpContext = this.state.Context;
		switch (pLevel)
		{
			case 'Global': return 0;
			case 'Customer': return tmpContext.IDCustomer;
			case 'Contract': return tmpContext.IDContract || 0;
			case 'Project': return tmpContext.IDProject || 0;
			case 'User': return tmpContext.IDUser;
			default: return 0;
		}
	}

	/**
	 * Fill the active context's customer/user from the live session when not already set. The provider
	 * initializes before login, so the session ids aren't known at construction — this self-heals the
	 * context on first use after CurrentUser is populated (super-user customer overrides are preserved).
	 */
	_ensureSessionContext()
	{
		const tmpContext = this.state.Context;
		if (!tmpContext.IDCustomer) { tmpContext.IDCustomer = this.idCustomer; }
		if (!tmpContext.IDUser) { tmpContext.IDUser = this.idUser; }
	}

	/**
	 * The ordered scope chain (general -> specific) for the active context. Contract/Project are
	 * included only when their context id is set. The deepest entry is the full effective merge.
	 * @return {Array<{Scope:String, ScopeID:Number}>}
	 */
	_activeScopeChain()
	{
		this._ensureSessionContext();
		const tmpContext = this.state.Context;
		const tmpChain = [ { Scope: 'Global', ScopeID: 0 }, { Scope: 'Customer', ScopeID: tmpContext.IDCustomer } ];
		if (tmpContext.IDContract) { tmpChain.push({ Scope: 'Contract', ScopeID: tmpContext.IDContract }); }
		if (tmpContext.IDProject) { tmpChain.push({ Scope: 'Project', ScopeID: tmpContext.IDProject }); }
		tmpChain.push({ Scope: 'User', ScopeID: tmpContext.IDUser });
		return tmpChain;
	}

	// --- REST helpers (promise wrappers over the Headlight rest client) ---

	_getJSON(pURL)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.getJSON(pURL, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	_postJSON(pURL, pPayload)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.postJSON(pURL, pPayload, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	_putJSON(pURL, pPayload)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.putJSON(pURL, pPayload, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	_deepClone(pValue)
	{
		return pValue ? JSON.parse(JSON.stringify(pValue)) : pValue;
	}

	// --- Configuration service: read one NamespaceValue's merged config at a scope ---

	/**
	 * Merged config for a NamespaceValue up to and including a scope. /Configuration/Get with a
	 * NamespaceValue returns { Config, LevelControlRecords }.
	 * @return {Promise<Object>}
	 */
	async _getConfigAt(pScope, pScopeID, pNamespaceKey, pNamespaceValue)
	{
		const tmpPayload =
		{
			LevelContext: pScope,
			LevelContextID: pScopeID,
			NamespaceKey: pNamespaceKey,
			NamespaceValue: pNamespaceValue,
		};
		const tmpResult = await this._postJSON('Configuration/Get', tmpPayload);
		if (tmpResult && tmpResult.Config !== undefined) { return tmpResult.Config || {}; }
		return (tmpResult && tmpResult.Document) || tmpResult || {};
	}

	/**
	 * List the live NamespaceValues for a key at the active deepest context (Get without a
	 * NamespaceValue returns an object keyed by NamespaceValue).
	 * @param {String} pNamespaceKey
	 * @return {Promise<Array<String>>}
	 */
	async loadNamespaceValuesAsync(pNamespaceKey)
	{
		const tmpDeepest = this._activeScopeChain().slice(-1)[0];
		const tmpResult = await this._postJSON('Configuration/Get',
			{ LevelContext: tmpDeepest.Scope, LevelContextID: tmpDeepest.ScopeID, NamespaceKey: pNamespaceKey });
		const tmpMap = (tmpResult && tmpResult.Document) || tmpResult || {};
		return Object.keys(tmpMap).filter((pKey) => pKey !== 'Config' && pKey !== 'LevelControlRecords');
	}

	/**
	 * Load a NamespaceValue's effective config + provenance map into AppData. Provenance is computed by
	 * diffing the merge at each successive scope (the deepest is the effective config to edit).
	 * @param {String} pNamespaceKey
	 * @param {String} pNamespaceValue
	 * @return {Promise<void>}
	 */
	async loadConfigAsync(pNamespaceKey, pNamespaceValue)
	{
		const tmpChain = this._activeScopeChain();
		const tmpMerges = [];
		for (let i = 0; i < tmpChain.length; i++)
		{
			/* eslint-disable no-await-in-loop */
			const tmpConfig = await this._getConfigAt(tmpChain[i].Scope, tmpChain[i].ScopeID, pNamespaceKey, pNamespaceValue);
			/* eslint-enable no-await-in-loop */
			tmpMerges.push({ Scope: tmpChain[i].Scope, ScopeID: tmpChain[i].ScopeID, Config: tmpConfig });
		}
		const tmpEffective = tmpMerges.length ? tmpMerges[tmpMerges.length - 1].Config : {};
		this.state.EditingConfig = this._deepClone(tmpEffective) || {};
		this.state.ProvenanceMap = libProvenance.computeProvenanceByDiff(tmpMerges);
	}

	/**
	 * Persist the (whole) edited config object at a scope. The service diffs vs the parent level:
	 * a value equal to the parent is dropped (no override); a key removed vs the parent becomes a
	 * hide-record (except at User scope). After writing we re-load so the badges reflect the truth.
	 * @param {String} pWriteScope
	 * @return {Promise<Object>} the server result
	 */
	async saveConfigAsync(pWriteScope)
	{
		const tmpSelected = this.state.Selected;
		if (!tmpSelected || tmpSelected.Mode !== 'config')
		{
			throw new Error('No configuration namespace selected.');
		}
		const tmpLevelID = this._levelIdFor(pWriteScope);
		const tmpURL = `Configuration/Put/${tmpSelected.NamespaceKey}/${tmpSelected.NamespaceValue}/${pWriteScope}/${tmpLevelID}`;
		const tmpResult = await this._postJSON(tmpURL, { Config: this.state.EditingConfig });
		await this.loadConfigAsync(tmpSelected.NamespaceKey, tmpSelected.NamespaceValue);
		return tmpResult;
	}

	// --- CustomProperties (flat per-record JSON; no cascade) ---

	/**
	 * Load a record's CustomProperties blob into the editor (badges disabled in the view).
	 * @param {String} pEntity 'Customer' | 'Project'
	 * @param {Number} pRecordID
	 * @return {Promise<void>}
	 */
	async loadCustomPropertiesAsync(pEntity, pRecordID)
	{
		const tmpRecord = await this._getJSON(`${pEntity}/${pRecordID}`);
		let tmpProps = {};
		const tmpRaw = tmpRecord && tmpRecord.CustomProperties;
		if (tmpRaw && typeof tmpRaw === 'string')
		{
			try { tmpProps = JSON.parse(tmpRaw); }
			catch (pError) { tmpProps = {}; }
		}
		else if (tmpRaw && typeof tmpRaw === 'object')
		{
			tmpProps = tmpRaw;
		}
		this.state.EditingConfig = tmpProps || {};
		this.state.ProvenanceMap = {}; // flat blob — no provenance
	}

	/**
	 * Persist the edited CustomProperties blob back to the record.
	 * @param {String} pEntity
	 * @param {Number} pRecordID
	 * @return {Promise<Object>}
	 */
	async saveCustomPropertiesAsync(pEntity, pRecordID)
	{
		const tmpPayload = { CustomProperties: JSON.stringify(this.state.EditingConfig || {}) };
		tmpPayload[`ID${pEntity}`] = pRecordID;
		return this._putJSON(`${pEntity}`, tmpPayload);
	}

	// --- selection + context (called from the view) ---

	async selectNamespaceAsync(pNamespaceKey, pNamespaceValue)
	{
		this.state.Loading = true;
		this.state.Message = '';
		try
		{
			let tmpValue = pNamespaceValue;
			if (!tmpValue)
			{
				const tmpValues = await this.loadNamespaceValuesAsync(pNamespaceKey);
				this.state.ValueList = tmpValues;
				tmpValue = tmpValues.length ? tmpValues[0] : '';
			}
			this.state.Selected =
			{
				Mode: 'config',
				NamespaceKey: pNamespaceKey,
				NamespaceValue: tmpValue,
				Label: `${libCatalog.labelForNamespace(pNamespaceKey)}${tmpValue ? ' › ' + tmpValue : ''}`,
			};
			if (tmpValue)
			{
				await this.loadConfigAsync(pNamespaceKey, tmpValue);
			}
			else
			{
				this.state.EditingConfig = {};
				this.state.ProvenanceMap = {};
				this.state.Message = 'No values configured for this namespace at the current context.';
			}
		}
		catch (pError)
		{
			this.state.Message = `Could not load configuration: ${pError.message}`;
		}
		this.state.Loading = false;
	}

	async selectCustomPropertiesAsync(pEntity, pRecordID, pLabel)
	{
		this.state.Loading = true;
		this.state.Message = '';
		try
		{
			this.state.Selected = { Mode: 'customprops', Entity: pEntity, RecordID: pRecordID, Label: pLabel };
			await this.loadCustomPropertiesAsync(pEntity, pRecordID);
		}
		catch (pError)
		{
			this.state.Message = `Could not load CustomProperties: ${pError.message}`;
		}
		this.state.Loading = false;
	}

	setWriteScope(pScope)
	{
		if (this.canWriteScope(pScope))
		{
			this.state.WriteScope = pScope;
		}
	}

	/**
	 * Whether the editor should be editable for the current selection + write scope.
	 * @return {Boolean}
	 */
	get isEditable()
	{
		const tmpSelected = this.state.Selected;
		if (!tmpSelected) { return false; }
		if (tmpSelected.Mode === 'customprops')
		{
			// meadow Update is Mine/MyCustomer — gate on customer-admin or super-user for now.
			return this.isSuperUser || this.hasCustomerAdmin;
		}
		return this.canWriteScope(this.state.WriteScope);
	}

	// --- help (dictionary) ---

	/**
	 * Dictionary text for a focused node path within the selected namespace.
	 * @param {String} pNodePath editor path
	 * @return {String}
	 */
	helpForNodePath(pNodePath)
	{
		const tmpSelected = this.state.Selected;
		if (!tmpSelected || tmpSelected.Mode !== 'config') { return ''; }
		const tmpAddress = libProvenance.editorPathToAddress(libProvenance.editorPathToLeafPath(pNodePath));
		return libDictionary.describeKey(tmpSelected.NamespaceKey, tmpAddress);
	}

	// --- derive view state ---

	deriveRenderState()
	{
		const tmpState = this.state;

		// Namespace rail — catalog grouped by Group, plus the CustomProperties pseudo-entries.
		const tmpGroups = {};
		libCatalog.CONFIG_NAMESPACE_CATALOG.forEach((pEntry) =>
		{
			const tmpGroupName = pEntry.Group || 'Other';
			if (!tmpGroups[tmpGroupName]) { tmpGroups[tmpGroupName] = { Group: tmpGroupName, Items: [] }; }
			tmpGroups[tmpGroupName].Items.push(
				{
					Kind: 'config',
					NamespaceKey: pEntry.NamespaceKey,
					Label: pEntry.Label,
					IsActive: !!(tmpState.Selected && tmpState.Selected.Mode === 'config'
						&& tmpState.Selected.NamespaceKey === pEntry.NamespaceKey),
					ActiveClass: (tmpState.Selected && tmpState.Selected.Mode === 'config'
						&& tmpState.Selected.NamespaceKey === pEntry.NamespaceKey) ? 'is-active' : '',
				});
		});
		const tmpRailGroups = Object.keys(tmpGroups).map((pKey) => tmpGroups[pKey]);
		// CustomProperties group (current customer; super-user can extend later).
		tmpRailGroups.push(
			{
				Group: 'Record Properties',
				Items:
				[
					{
						Kind: 'customprops',
						Entity: 'Customer',
						RecordID: this.idCustomer,
						Label: 'Customer CustomProperties',
						IsActive: !!(tmpState.Selected && tmpState.Selected.Mode === 'customprops'),
						ActiveClass: (tmpState.Selected && tmpState.Selected.Mode === 'customprops') ? 'is-active' : '',
					},
				],
			});
		tmpState.RailGroups = tmpRailGroups;

		// Write-scope options (gated).
		tmpState.ScopeOptions = WRITE_SCOPES
			.filter((pScope) => pScope !== 'Project' || tmpState.Context.IDProject)
			.map((pScope) => (
				{
					Value: pScope,
					Label: this._scopeLabel(pScope),
					SelectedAttr: (pScope === tmpState.WriteScope) ? 'selected' : '',
					DisabledAttr: this.canWriteScope(pScope) ? '' : 'disabled',
				}));

		// Value list for the selected namespace.
		tmpState.ValueOptions = (tmpState.ValueList || []).map((pValue) => (
			{
				Value: pValue,
				SelectedAttr: (tmpState.Selected && tmpState.Selected.NamespaceValue === pValue) ? 'selected' : '',
			}));

		// Editor mode flags + help.
		const tmpIsConfig = !!(tmpState.Selected && tmpState.Selected.Mode === 'config');
		const tmpEditable = this.isEditable;
		tmpState.IsConfigMode = tmpIsConfig;
		tmpState.IsCustomPropsMode = !!(tmpState.Selected && tmpState.Selected.Mode === 'customprops');
		tmpState.Editable = tmpEditable;
		tmpState.HelpNamespace = tmpIsConfig ? libDictionary.describeNamespace(tmpState.Selected.NamespaceKey) : '';
		tmpState.SaveLabel = tmpIsConfig ? `Save to ${this._scopeLabel(tmpState.WriteScope)}` : 'Save';

		// Dictionary key list for the help panel (the "what it means" overlay).
		tmpState.HelpKeyList = [];
		if (tmpIsConfig)
		{
			const tmpDict = libDictionary.CONFIG_DICTIONARY[tmpState.Selected.NamespaceKey];
			const tmpKeys = (tmpDict && tmpDict.Keys) || {};
			tmpState.HelpKeyList = Object.keys(tmpKeys).map((pAddress) => ({ Address: pAddress, Text: tmpKeys[pAddress] }));
		}

		// Scope legend (the colour key for the badges), one per scope in the active chain.
		tmpState.Legend = this._activeScopeChain().map((pLevel) => (
			{
				Label: this._scopeLabel(pLevel.Scope),
				BadgeClass: 'cfg-prov-' + String(pLevel.Scope).toLowerCase(),
			}));

		// conditional slots (single-element-array trick)
		const tmpHasValue = !!(tmpState.Selected && tmpState.Selected.NamespaceValue);
		const tmpHasEditable = (tmpIsConfig && tmpHasValue) || tmpState.IsCustomPropsMode;
		tmpState.LoadingSlot = tmpState.Loading ? [ {} ] : [];
		tmpState.MessageSlot = tmpState.Message ? [ { Message: tmpState.Message } ] : [];
		tmpState.SelectedSlot = tmpState.Selected ? [ tmpState.Selected ] : [];
		tmpState.EmptySlot = tmpState.Selected ? [] : [ {} ];
		tmpState.ScopePickerSlot = (tmpIsConfig && tmpHasValue) ? [ {} ] : [];
		tmpState.EditorBodySlot = tmpHasEditable ? [ {} ] : [];
		tmpState.NoValuesSlot = (tmpIsConfig && !tmpHasValue) ? [ {} ] : [];
		tmpState.ValuePickerSlot = (tmpIsConfig && (tmpState.ValueList || []).length > 1) ? [ {} ] : [];
		tmpState.SaveEnabledSlot = (tmpEditable && tmpHasEditable) ? [ { SaveLabel: tmpState.SaveLabel } ] : [];
		tmpState.ReadOnlySlot = (tmpHasEditable && !tmpEditable) ? [ {} ] : [];
		tmpState.HelpSlot = (tmpState.HelpNamespace || tmpState.HelpKeyList.length) ? [ {} ] : [];

		return tmpState;
	}
}

module.exports = ConfigViewerProvider;
module.exports.default_configuration =
{
	ProviderIdentifier: 'ConfigViewer',
	AutoInitialize: true,
	AutoInitializeOrdinal: 40,
};
